import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { takeOAuthDestination } from "@/lib/oauthFlow";
import { translateAuthError } from "@/lib/authErrorTranslations";

/**
 * Pantalla pública de regreso del inicio de sesión con Google / Apple.
 * Espera a que la sesión quede lista y recién entonces navega al destino.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    let done = false;

    const fail = (raw: string) => {
      if (done) return;
      done = true;
      const message = translateAuthError(raw) || raw;
      navigate(`/auth?authError=${encodeURIComponent(message)}`, { replace: true });
    };

    const succeed = () => {
      if (done) return;
      done = true;
      navigate(takeOAuthDestination(), { replace: true });
    };

    // Errores devueltos por el proveedor en la URL (query o hash)
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const errorCode =
      search.get("error_code") ||
      search.get("error") ||
      hash.get("error_code") ||
      hash.get("error");
    const errorDesc =
      search.get("error_description") || hash.get("error_description");

    if (errorCode) {
      fail(errorDesc || errorCode);
      return;
    }

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) succeed();
    });

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) succeed();
    });

    const slowTimer = setTimeout(() => setSlow(true), 4000);
    const timeout = setTimeout(() => {
      fail("State verification failed");
    }, 12000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timeout);
      clearTimeout(slowTimer);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      <p className="text-lg font-medium">Entrando a tu cuenta…</p>
      {slow && (
        <p className="text-sm text-muted-foreground max-w-sm">
          Esto está tardando un poco. No cierres esta pantalla.
        </p>
      )}
    </div>
  );
};

export default AuthCallback;
